import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { HttpHelper } from '@app/common/http-helper';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { Constants } from '@app/common/constants';
import { PowerBIFrame } from '@app/models/power-bi-frame';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class PowerBIService {
  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private http: HttpClient,
              private httpHelper: HttpHelper,
              private cache: CacheService) { }

  private readonly cacheKey = 'PowerBIService-getPowerBIFrames';
  public useCache = true;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  getAllPowerBIFrames$: Observable<Array<PowerBIFrame>>;

  public reload() {
    this.getAllPowerBIFrames$ = null;
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
    }
    this.reload$.next();
    this.refresh$.next(null);
  }

  getAllPowerBIFrames(): Observable<Array<PowerBIFrame>> {
    if (this.getAllPowerBIFrames$) {
      return this.getAllPowerBIFrames$;
    } else {
      this.getAllPowerBIFrames$ = this.http.get<Array<PowerBIFrame>>(`${Constants.API_ENDPOINT}/powerbiframes`)
        .pipe(
          map(response => {
            console.log(`fetched result getAllPowerBIFrames: `, response);
            return response.map(p => new PowerBIFrame(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(`${this.cacheKey}-all`, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllPowerBIFrames', result: new Array<PowerBIFrame>(), cacheKey: `${this.cacheKey}-all` })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<PowerBIFrame>>(`${this.cacheKey}-all`);
        if (cachedData) {
          return concat(of(cachedData), this.getAllPowerBIFrames$);
        }
      }
      return this.getAllPowerBIFrames$;
    }
  }

  getPowerBIFrame(id: number): Observable<PowerBIFrame> {
    return this.http.get<PowerBIFrame>(`${Constants.API_ENDPOINT}/powerbiframes/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result getPowerBIFrame: `, response);
          return new PowerBIFrame(response);
        }));
  }

  updatePowerBIFrame(id: number, frame: PowerBIFrame): Observable<PowerBIFrame> {
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<PowerBIFrame>(`${Constants.API_ENDPOINT}/powerbiframes/${id}`, frame, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return frame;
        }));

    return fetchOperation;
  }

  addPowerBIFrame(frame: PowerBIFrame): Observable<PowerBIFrame> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<PowerBIFrame>(`${Constants.API_ENDPOINT}/powerbiframes`, frame, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`added result: `);
          console.log(response);
          this.reload();
          return new PowerBIFrame(response);
        }));

    return fetchOperation;
  }

  deletePowerBIFrame(id: number): Observable<PowerBIFrame> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<PowerBIFrame>(`${Constants.API_ENDPOINT}/powerbiframes/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deletePowerBIFrame', result: new PowerBIFrame() })));

    return fetchOperation;
  }

}
